/**
 * @fileoverview Custom Horizontal Bar Chart with clear user labels
 * @description Renders usernames on the left in a fixed column, and
 *              animated horizontal bars on the right. Supports single
 *              or stacked bars with tooltips and value labels.
 */

import React, { useState } from 'react';
import './UserBarChart.css';

/**
 * @param {Object} props
 * @param {Array}  props.data       — [{ username, ...values }]
 * @param {Array}  props.bars       — [{ key, label, color }]  (one per bar series)
 * @param {Function} [props.valueFormatter] — (value, key) => string
 * @param {string}   [props.emptyMessage]
 * @param {number}   [props.maxItems]       — limit displayed rows (default all)
 */
const UserBarChart = ({
  data = [],
  bars = [],
  valueFormatter,
  emptyMessage = 'No data available',
  maxItems,
}) => {
  const [hoveredRow, setHoveredRow] = useState(null);
  const [tooltipData, setTooltipData] = useState(null);

  const rows = maxItems ? data.slice(0, maxItems) : data;

  // Compute the max total value across all rows (for bar scaling)
  const maxVal = rows.reduce((max, row) => {
    const total = bars.reduce((s, b) => s + (Number(row[b.key]) || 0), 0);
    return Math.max(max, total);
  }, 0);

  if (!rows.length) {
    return (
      <div className="ubc-empty">
        <span>{emptyMessage}</span>
      </div>
    );
  }

  const formatVal = (val, key) => {
    if (valueFormatter) return valueFormatter(val, key);
    if (typeof val === 'number') return val.toLocaleString();
    return String(val ?? '—');
  };

  return (
    <div className="ubc-root">
      {/* Legend (only if multiple series) */}
      {bars.length > 1 && (
        <div className="ubc-legend">
          {bars.map((b) => (
            <div key={b.key} className="ubc-legend-item">
              <span className="ubc-legend-dot" style={{ background: b.color }} />
              <span className="ubc-legend-label">{b.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Rows */}
      <div className="ubc-rows">
        {rows.map((row, idx) => {
          const isHovered = hoveredRow === idx;
          return (
            <div
              key={row.username}
              className={`ubc-row ${isHovered ? 'ubc-row--hover' : ''}`}
              onMouseEnter={() => {
                setHoveredRow(idx);
                setTooltipData(row);
              }}
              onMouseLeave={() => {
                setHoveredRow(null);
                setTooltipData(null);
              }}
            >
              {/* Rank number */}
              <div className="ubc-rank">{idx + 1}</div>

              {/* Username label */}
              <div className="ubc-username" title={row.githubUsername ? `@${row.githubUsername}` : row.username}>
                {row.username}
              </div>

              {/* Bar area */}
              <div className="ubc-bar-track">
                {bars.map((b) => {
                  const val = Number(row[b.key]) || 0;
                  const pct = maxVal > 0 ? (val / maxVal) * 100 : 0;
                  return (
                    <div
                      key={b.key}
                      className="ubc-bar-segment"
                      style={{
                        width: `${Math.max(pct, val > 0 ? 2 : 0)}%`,
                        backgroundColor: b.color,
                        opacity: isHovered ? 1 : 0.85,
                      }}
                    />
                  );
                })}
              </div>

              {/* Value(s) at the end */}
              <div className="ubc-value">
                {bars.length === 1
                  ? formatVal(row[bars[0].key], bars[0].key)
                  : bars.map((b, i) => (
                      <span key={b.key} style={{ color: b.color }}>
                        {formatVal(row[b.key], b.key)}
                        {i < bars.length - 1 && (
                          <span className="ubc-value-sep">/</span>
                        )}
                      </span>
                    ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Tooltip (floating, follows hovered row) */}
      {tooltipData && bars.length > 1 && (
        <div className="ubc-tooltip">
          <div className="ubc-tooltip-user">{tooltipData.username}{tooltipData.githubUsername && <span style={{ opacity: 0.7, marginLeft: 6, fontSize: '0.85em' }}>@{tooltipData.githubUsername}</span>}</div>
          {bars.map((b) => (
            <div key={b.key} className="ubc-tooltip-row">
              <span className="ubc-tooltip-dot" style={{ background: b.color }} />
              <span className="ubc-tooltip-label">{b.label}:</span>
              <span className="ubc-tooltip-val">{formatVal(tooltipData[b.key], b.key)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserBarChart;
